<script setup>
import IconHubCheck from '../../icons/IconHubCheck.vue';
import XlButton from '../../xl/XlButton.vue';
import XlCard from '../../xl/XlCard.vue';
import XlSkeleton from '../../xl/XlSkeleton.vue';

const props = defineProps({
  steps: { type: Array, required: true },
  mode: { type: String, default: 'page', validator: (v) => ['page', 'region'].includes(v) },
  footer: { type: String, default: '' },
  footerAction: { type: Object, default: null },
});

const emit = defineEmits(['action', 'hide']);
</script>

<template>
  <div>
    <XlCard class="setup" data-xl-gen="northstar" data-testid="setup-checklist" :data-mode="props.mode">
      <ol class="setup__steps">
        <li v-for="step in props.steps" :key="step.id" class="setup__step" :data-testid="`setup-step-${step.id}`">
          <XlSkeleton v-if="step.done === null" class="setup__mark" width="32px" height="32px" radius="13px" data-testid="setup-step-loading" />
          <span v-else class="setup__mark" :class="step.done ? 'setup__mark--done' : 'setup__mark--open'">
            <template v-if="step.done">
              <IconHubCheck :size="16" />
              <span class="setup__sr">Done</span>
            </template>
            <template v-else>{{ step.n }}</template>
          </span>
          <div class="setup__text">
            <b class="setup__title">{{ step.title }}</b>
            <span class="setup__body">{{ step.body }}</span>
          </div>
          <XlButton
            v-for="action in (step.actions ?? [])"
            :key="action.id"
            size="sm"
            :variant="action.primary ? 'filled' : 'ghost'"
            :data-testid="`setup-action-${action.id}`"
            @click="emit('action', action.id)"
          >{{ action.label }}</XlButton>
        </li>
      </ol>
      <div v-if="props.mode === 'region'" class="setup__hide">
        <XlButton size="sm" variant="ghost" aria-label="Hide the setup checklist" data-testid="setup-hide" @click="emit('hide')">Hide this</XlButton>
      </div>
    </XlCard>
    <p v-if="props.mode === 'page' && props.footer" class="setup__footer" data-xl-gen="northstar" data-testid="setup-footer">{{ props.footer }}</p>
    <XlButton
      v-if="props.mode === 'page' && props.footerAction"
      class="setup__footer-action"
      size="sm"
      variant="ghost"
      :data-testid="`setup-action-${props.footerAction.id}`"
      @click="emit('action', props.footerAction.id)"
    >{{ props.footerAction.label }}</XlButton>
  </div>
</template>

<style scoped>
.setup { max-width: 640px; padding: 22px 24px; color: var(--xl-color-primary-text); font-family: var(--xl-font-family); }
.setup__steps { list-style: none; margin: 0; padding: 0; }
.setup__step {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #F6F2F8;
}
.setup__step:last-child { border-bottom: 0; }
.setup__mark {
  position: relative;
  display: grid;
  place-items: center;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 13px;
  font-size: 13px;
  font-weight: 700;
}
.setup__mark--open { background: var(--xl-color-primary-container); color: var(--xl-color-primary); }
.setup__mark--done { background: var(--xl-color-success-container, var(--xl-color-border)); color: var(--xl-color-success, var(--xl-color-secondary-text)); }
.setup__text { flex: 1; min-width: 0; padding-right: 12px; }
.setup__title { display: block; font-size: 14.5px; color: var(--xl-color-primary-text); }
.setup__body { white-space: normal; font-size: 13px; line-height: 1.5; color: #756D7C; }
.setup__footer { min-height: 0; max-width: 60ch; margin: 16px 0 0; background: none; font-size: 13px; color: #756D7C; }
.setup__footer-action { margin-top: 12px; }
.setup__hide { display: flex; justify-content: flex-end; padding: 10px 0 12px; border-top: 1px solid var(--xl-color-border); }
.setup__sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
</style>
